/**
 * Timezone Utility Module for DayTradingBench (Frontend)
 *
 * All timestamps are stored and displayed in UTC throughout the application.
 * This simplifies the codebase and ensures consistency with API data.
 */

export const TIMEZONE = 'UTC';

/**
 * Format a date for display in UTC
 * Main formatting function used throughout the app
 * @param {Date|string} date - Date in UTC
 * @param {Object} options - Formatting options
 * @param {boolean} options.includeDate - Include the date portion (default: true)
 * @param {boolean} options.includeTime - Include the time portion (default: true)
 * @param {boolean} options.includeTimezone - Include timezone indicator (default: true)
 * @param {boolean} options.use24Hour - Use 24-hour format (default: false)
 * @param {boolean} options.shortDate - Use shorter date format (default: false)
 * @returns {string} - Formatted date string (e.g., "Dec 6, 2024 10:30 AM UTC")
 */
export function formatForDisplay(date, options = {}) {
  if (!date) return '';

  const {
    includeDate = true,
    includeTime = true,
    includeTimezone = true,
    use24Hour = false,
    shortDate = false
  } = options;

  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';

  const formatOptions = {
    timeZone: TIMEZONE,
  };

  if (includeDate) {
    formatOptions.month = shortDate ? 'numeric' : 'short';
    formatOptions.day = 'numeric';
    if (!shortDate) {
      formatOptions.year = 'numeric';
    }
  }

  if (includeTime) {
    formatOptions.hour = 'numeric';
    formatOptions.minute = '2-digit';
    formatOptions.hour12 = !use24Hour;
  }

  const formatter = new Intl.DateTimeFormat('en-US', formatOptions);
  let formatted = formatter.format(d);

  if (includeTimezone) {
    formatted += ' UTC';
  }

  return formatted;
}

/**
 * Format a date as ISO string date portion
 * Useful for date pickers that need YYYY-MM-DD format
 * @param {Date|string} date - Date in UTC
 * @returns {string} - ISO date string (e.g., "2024-12-06")
 */
export function formatDateOnly(date) {
  if (!date) return '';

  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';

  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });

  return formatter.format(d);
}

/**
 * Format time only in UTC
 * @param {Date|string} date - Date in UTC
 * @param {Object} options - Formatting options
 * @param {boolean} options.includeSeconds - Include seconds (default: false)
 * @param {boolean} options.includeTimezone - Include timezone indicator (default: true)
 * @returns {string} - Time string (e.g., "10:30 AM UTC")
 */
export function formatTimeOnly(date, options = {}) {
  if (!date) return '';

  const { includeSeconds = false, includeTimezone = true } = options;

  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';

  const formatOptions = {
    timeZone: TIMEZONE,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  };

  if (includeSeconds) {
    formatOptions.second = '2-digit';
  }

  const formatter = new Intl.DateTimeFormat('en-US', formatOptions);
  let formatted = formatter.format(d);

  if (includeTimezone) {
    formatted += ' UTC';
  }

  return formatted;
}

/**
 * Format date and time for display (short format)
 * Commonly used in trade lists and tables
 * @param {Date|string} date - Date in UTC
 * @returns {string} - Formatted string (e.g., "12/6 10:30 AM UTC")
 */
export function formatShortDateTime(date) {
  return formatForDisplay(date, {
    includeDate: true,
    includeTime: true,
    includeTimezone: true,
    shortDate: true
  });
}

/**
 * Format full date and time for display
 * Used for detailed views
 * @param {Date|string} date - Date in UTC
 * @returns {string} - Formatted string (e.g., "Dec 6, 2024 10:30 AM UTC")
 */
export function formatFullDateTime(date) {
  return formatForDisplay(date, {
    includeDate: true,
    includeTime: true,
    includeTimezone: true,
    shortDate: false
  });
}

/**
 * Get Unix timestamp in seconds for chart libraries
 * @param {Date|string} date - Date in UTC
 * @returns {number} - Unix timestamp in seconds
 */
export function toChartTimestamp(date) {
  if (!date) return 0;
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return 0;

  return Math.floor(d.getTime() / 1000);
}

/**
 * Get current time in UTC as formatted string
 * @returns {string} - Current time (e.g., "10:30 AM UTC")
 */
export function nowInUTC() {
  return formatForDisplay(new Date());
}

/**
 * Format for datetime-local input (YYYY-MM-DDTHH:MM)
 * Used for date/time picker inputs - displays UTC time
 * @param {Date|string} date - Date in UTC
 * @returns {string} - Formatted string for input
 */
export function formatForDateTimeInput(date) {
  if (!date) return '';

  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';

  // Get components in UTC
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  const hour = String(d.getUTCHours()).padStart(2, '0');
  const minute = String(d.getUTCMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hour}:${minute}`;
}

/**
 * Parse a datetime-local input value (YYYY-MM-DDTHH:MM) as UTC
 * and convert to ISO string for sending to server
 * @param {string} inputValue - Value from datetime-local input (interpreted as UTC)
 * @returns {string} - ISO string in UTC
 */
export function parseFromDateTimeInput(inputValue) {
  if (!inputValue) return '';

  // Parse the input value as UTC
  const [datePart, timePart] = inputValue.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute] = timePart.split(':').map(Number);

  // Create UTC date directly
  const utcDate = new Date(Date.UTC(year, month - 1, day, hour, minute, 0, 0));

  return utcDate.toISOString();
}

/**
 * Get timezone abbreviation (always UTC now)
 * @returns {string} - Timezone abbreviation
 */
export function getTimezoneAbbreviation() {
  return 'UTC';
}

// Legacy exports for backwards compatibility
export const NEW_YORK_TIMEZONE = TIMEZONE;
export const convertToNewYork = (date) => date;
export const isMarketHours = () => true; // USTEC/US100 trades nearly 24/5 via CFD
export const nowInNewYork = nowInUTC;

// Default export for convenience
export default {
  TIMEZONE,
  formatForDisplay,
  formatDateOnly,
  formatTimeOnly,
  formatShortDateTime,
  formatFullDateTime,
  toChartTimestamp,
  getTimezoneAbbreviation,
  nowInUTC,
  formatForDateTimeInput,
  parseFromDateTimeInput,
  // Legacy
  NEW_YORK_TIMEZONE,
  convertToNewYork,
  isMarketHours,
  nowInNewYork
};
